/**
 * Parsing of the run's own `summary` JSON (ADR-010 §3, A23 (c): the terminal
 * verdict lives in `summary.outcome` — there is no `outcome` column).
 *
 * The wire type is `Record<string, unknown> | null` (shared's
 * `testRunDetailSchema`), so every field the screen renders is narrowed with
 * explicit guards — no casts (conventions §Type-safety). The shape guarded
 * here is the epic's pre-resolved summary contract: `{ outcome, mode,
 * testEmail, verdicts: [{ rule, pass, reason? }], attempts, distribution?,
 * error?, reason? }`.
 */

import type { TestRunOutcome } from '@leads-platform/shared';

/** One rule verdict of the evaluation (`summary.verdicts[]`). */
export interface RunVerdict {
  rule: string;
  pass: boolean;
  reason: string | null;
}

/** The parts of `summary` the run detail renders. */
export interface ParsedRunSummary {
  outcome: TestRunOutcome | null;
  verdicts: RunVerdict[];
  /** Pre-attempt infrastructure fault (the run `status = failed` case). */
  error: string | null;
  /** Failure reason of a completed evaluation (e.g. `cto_window_expired`). */
  reason: string | null;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function asVerdict(value: unknown): RunVerdict | null {
  if (typeof value !== 'object' || value === null) return null;
  if (!('rule' in value) || !('pass' in value)) return null;
  const rule = asString(value.rule);
  if (!rule) return null;
  return {
    rule,
    pass: value.pass === true,
    reason: 'reason' in value ? asString(value.reason) : null,
  };
}

/** Narrow the run's `summary` JSON down to what the detail renders. */
export function parseRunSummary(summary: Record<string, unknown> | null): ParsedRunSummary {
  const empty: ParsedRunSummary = { outcome: null, verdicts: [], error: null, reason: null };
  if (summary === null) return empty;

  const outcome =
    summary.outcome === 'passed' || summary.outcome === 'failed' ? summary.outcome : null;

  const verdicts: RunVerdict[] = [];
  if (Array.isArray(summary.verdicts)) {
    for (const entry of summary.verdicts) {
      const verdict = asVerdict(entry);
      if (verdict) verdicts.push(verdict);
    }
  }

  return {
    outcome,
    verdicts,
    error: asString(summary.error),
    reason: asString(summary.reason),
  };
}
